/**
 * Talent Tree Canvas Renderer
 *
 * Handles all canvas-based rendering for talent trees:
 * - Background shapes (pickaxe, potion, tree, etc.)
 * - Node drawing with state-based styling
 * - Connection lines between nodes
 * - Zoom and pan controls
 * - Glow effects and animations
 */

import { getTalentTreeData, NODE_STATES } from './talentTreeData.js';
import { getNodeState, getNodeRank, getAvailablePoints } from './talentTree.js';

// Visual constants
const NODE_RADIUS = 28;
const NODE_BORDER = 3;
const CONNECTION_WIDTH = 3;

// Colors
const COLORS = {
    // Node states
    locked: '#2a2a3e',
    lockedBorder: '#3a3a4e',
    available: '#4a5568',
    availableBorder: '#68d391',
    availableGlow: 'rgba(104, 211, 145, 0.4)',
    purchased: '#2d3748',
    purchasedBorder: '#63b3ed',
    purchasedGlow: 'rgba(99, 179, 237, 0.3)',
    maxed: '#1a1a2e',
    maxedBorder: '#9f7aea',
    maxedGlow: 'rgba(159, 122, 234, 0.5)',

    // Pending refund state
    pending: '#3d2a2a',
    pendingBorder: '#ef4444',
    pendingGlow: 'rgba(239, 68, 68, 0.5)',

    // Connections
    connectionLocked: '#3a3a4e',
    connectionUnlocked: '#63b3ed',

    // Text
    text: '#e2e8f0',
    textSecondary: '#a0aec0',

    // Background
    background: '#1a1a2e'
};

/**
 * Talent Tree Renderer Class
 */
export class TalentTreeRenderer {
    constructor(canvas, skillId) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.skillId = skillId;
        this.tree = getTalentTreeData(skillId);

        // View state
        this.scale = 1;
        this.offset = { x: 0, y: 0 };
        this.isDragging = false;
        this.lastMouse = { x: 0, y: 0 };
        this.dragDistance = 0; // Track total drag distance to distinguish click vs drag

        // Animation state
        this.animationFrame = null;
        this.glowPhase = 0;

        // Setup canvas size to fill container
        this.resizeCanvas();

        // Handle window resize
        this.resizeHandler = () => this.resizeCanvas();
        window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Resize canvas to fill container and center the tree
     */
    resizeCanvas() {
        const container = this.canvas.parentElement;
        if (!container) return;

        // Set canvas to container size
        const rect = container.getBoundingClientRect();
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;

        // Center the tree in the canvas
        if (this.tree) {
            const treeWidth = this.tree.canvasSize.width;
            const treeHeight = this.tree.canvasSize.height;

            // Calculate offset to center the tree
            this.offset.x = (this.canvas.width - treeWidth) / 2;
            this.offset.y = (this.canvas.height - treeHeight) / 2;
        }

        this.render();
    }

    /**
     * Main render function
     */
    render() {
        if (!this.tree) return;

        const ctx = this.ctx;

        // Clear canvas
        ctx.fillStyle = COLORS.background;
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Apply transform
        ctx.save();
        ctx.translate(this.offset.x, this.offset.y);
        ctx.scale(this.scale, this.scale);

        // Draw background shape
        this.drawBackgroundShape();

        // Draw connections first (behind nodes)
        this.drawConnections();

        // Draw nodes
        this.drawNodes();

        ctx.restore();

        // Update glow animation
        this.glowPhase += 0.05;
    }

    /**
     * Draw the background shape placeholder for this skill
     */
    drawBackgroundShape() {
        const ctx = this.ctx;
        const { width, height } = this.tree.canvasSize;

        // Draw a subtle skill-themed background
        ctx.save();
        ctx.globalAlpha = 0.1;
        ctx.strokeStyle = this.tree.accentColor || '#4a5568';
        ctx.lineWidth = 2;

        switch (this.tree.shape) {
            case 'pickaxe':
                this.drawPickaxeShape(width / 2, height / 2);
                break;
            case 'tree':
                this.drawTreeShape(width / 2, height / 2);
                break;
            case 'potion':
                this.drawPotionShape(width / 2, height / 2);
                break;
            default:
                // Generic circular pattern
                this.drawGenericShape(width / 2, height / 2);
        }

        ctx.restore();
    }

    /**
     * Draw pickaxe background shape (Mining)
     */
    drawPickaxeShape(cx, cy) {
        const ctx = this.ctx;

        // Handle (vertical line from bottom)
        ctx.beginPath();
        ctx.moveTo(cx, cy + 150);
        ctx.lineTo(cx, cy - 50);
        ctx.stroke();

        // Pickaxe head (angled lines)
        ctx.beginPath();
        ctx.moveTo(cx - 120, cy - 120);
        ctx.lineTo(cx, cy - 50);
        ctx.lineTo(cx + 120, cy - 120);
        ctx.stroke();

        // Pick points
        ctx.beginPath();
        ctx.moveTo(cx - 120, cy - 120);
        ctx.lineTo(cx - 150, cy - 150);
        ctx.moveTo(cx + 120, cy - 120);
        ctx.lineTo(cx + 150, cy - 150);
        ctx.stroke();
    }

    /**
     * Draw tree background shape (Combat)
     */
    drawTreeShape(cx, cy) {
        const ctx = this.ctx;

        // Trunk
        ctx.beginPath();
        ctx.moveTo(cx, cy + 150);
        ctx.lineTo(cx, cy - 50);
        ctx.stroke();

        // Branches (4 directions)
        const branchY = cy - 100;
        ctx.beginPath();
        ctx.moveTo(cx, cy - 50);
        ctx.lineTo(cx - 100, branchY);
        ctx.moveTo(cx, cy - 50);
        ctx.lineTo(cx + 100, branchY);
        ctx.moveTo(cx, cy - 50);
        ctx.lineTo(cx, branchY - 50);
        ctx.stroke();
    }

    /**
     * Draw potion bottle shape (Alchemy)
     */
    drawPotionShape(cx, cy) {
        const ctx = this.ctx;

        // Bottle body (rounded rectangle)
        ctx.beginPath();
        ctx.ellipse(cx, cy + 50, 80, 100, 0, 0, Math.PI * 2);
        ctx.stroke();

        // Neck
        ctx.beginPath();
        ctx.moveTo(cx - 30, cy - 50);
        ctx.lineTo(cx - 30, cy - 100);
        ctx.lineTo(cx + 30, cy - 100);
        ctx.lineTo(cx + 30, cy - 50);
        ctx.stroke();

        // Cork
        ctx.beginPath();
        ctx.rect(cx - 25, cy - 130, 50, 30);
        ctx.stroke();
    }

    /**
     * Draw generic circular pattern
     */
    drawGenericShape(cx, cy) {
        const ctx = this.ctx;

        // Concentric circles
        for (let r = 50; r <= 200; r += 50) {
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.stroke();
        }

        // Cross lines
        ctx.beginPath();
        ctx.moveTo(cx - 200, cy);
        ctx.lineTo(cx + 200, cy);
        ctx.moveTo(cx, cy - 200);
        ctx.lineTo(cx, cy + 200);
        ctx.stroke();
    }

    /**
     * Draw all connection lines
     */
    drawConnections() {
        const ctx = this.ctx;

        for (const connection of this.tree.connections) {
            const fromNode = this.tree.nodes[connection.from];
            const toNode = this.tree.nodes[connection.to];

            if (!fromNode || !toNode) continue;

            const fromState = getNodeState(this.skillId, connection.from);
            const toState = getNodeState(this.skillId, connection.to);

            // Connection is "active" if from node is purchased
            const isActive = fromState === NODE_STATES.PURCHASED || fromState === NODE_STATES.MAXED;

            ctx.beginPath();
            ctx.strokeStyle = isActive ? COLORS.connectionUnlocked : COLORS.connectionLocked;
            ctx.lineWidth = CONNECTION_WIDTH;

            if (connection.style === 'curved') {
                // Bezier curve
                const midX = (fromNode.position.x + toNode.position.x) / 2;
                const midY = (fromNode.position.y + toNode.position.y) / 2;
                ctx.moveTo(fromNode.position.x, fromNode.position.y);
                ctx.quadraticCurveTo(midX, fromNode.position.y, toNode.position.x, toNode.position.y);
            } else {
                // Straight line
                ctx.moveTo(fromNode.position.x, fromNode.position.y);
                ctx.lineTo(toNode.position.x, toNode.position.y);
            }

            ctx.stroke();
        }
    }

    /**
     * Draw all nodes
     */
    drawNodes() {
        for (const [nodeId, node] of Object.entries(this.tree.nodes)) {
            const state = getNodeState(this.skillId, nodeId);
            const rank = getNodeRank(this.skillId, nodeId);
            const pendingRanks = window.talentTreeUI?.isNodePendingRefund(nodeId) || 0;
            this.drawNode(node, nodeId, state, rank, pendingRanks);
        }
    }

    /**
     * Draw a single node
     */
    drawNode(node, nodeId, state, currentRank, pendingRanks = 0) {
        const ctx = this.ctx;
        const { x, y } = node.position;

        // Get colors based on state (or pending if marked for refund)
        const isPending = pendingRanks > 0;
        const colors = isPending ? this.getPendingColors() : this.getNodeColors(state);

        // Draw glow effect for available/purchased/maxed
        if (state !== NODE_STATES.LOCKED && colors.glow) {
            const glowIntensity = 0.5 + Math.sin(this.glowPhase) * 0.2;
            ctx.beginPath();
            const gradient = ctx.createRadialGradient(x, y, NODE_RADIUS, x, y, NODE_RADIUS + 15);
            // Replace the alpha value in the rgba color string
            const glowColor = colors.glow.replace(/[\d.]+\)$/, `${glowIntensity})`);
            gradient.addColorStop(0, glowColor);
            gradient.addColorStop(1, 'transparent');
            ctx.fillStyle = gradient;
            ctx.arc(x, y, NODE_RADIUS + 15, 0, Math.PI * 2);
            ctx.fill();
        }

        // Draw node background
        ctx.beginPath();
        ctx.arc(x, y, NODE_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = colors.fill;
        ctx.fill();

        // Draw border
        ctx.beginPath();
        ctx.arc(x, y, NODE_RADIUS, 0, Math.PI * 2);
        ctx.strokeStyle = colors.border;
        ctx.lineWidth = NODE_BORDER;
        ctx.stroke();

        // Draw lock icon if locked
        if (state === NODE_STATES.LOCKED) {
            this.drawLockIcon(x, y);
            // Show rank below lock for multi-rank nodes
            if (node.maxRank > 1) {
                ctx.fillStyle = COLORS.textSecondary;
                ctx.font = 'bold 9px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(`0/${node.maxRank}`, x, y + 14);
            }
        } else {
            // Short abbreviation from name
            const abbrev = node.name.split(' ').map(w => w[0]).join('').slice(0, 3);

            if (node.maxRank > 1) {
                // Multi-rank: abbreviation on top, rank below
                ctx.fillStyle = COLORS.text;
                ctx.font = 'bold 11px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(abbrev, x, y - 6);

                // Rank indicator inside node
                const rankText = pendingRanks > 0
                    ? `${currentRank}→${currentRank - pendingRanks}/${node.maxRank}`
                    : `${currentRank}/${node.maxRank}`;
                ctx.fillStyle = pendingRanks > 0 ? '#ef4444' : (state === NODE_STATES.MAXED ? COLORS.maxedBorder : COLORS.textSecondary);
                ctx.font = 'bold 9px Arial';
                ctx.fillText(rankText, x, y + 10);
            } else {
                // Single-rank: just centered abbreviation
                ctx.fillStyle = COLORS.text;
                ctx.font = 'bold 12px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(abbrev, x, y);
            }
        }
    }

    /**
     * Get colors for a node based on state
     */
    getNodeColors(state) {
        switch (state) {
            case NODE_STATES.LOCKED:
                return {
                    fill: COLORS.locked,
                    border: COLORS.lockedBorder,
                    glow: null
                };
            case NODE_STATES.AVAILABLE:
                return {
                    fill: COLORS.available,
                    border: COLORS.availableBorder,
                    glow: COLORS.availableGlow
                };
            case NODE_STATES.PURCHASED:
                return {
                    fill: COLORS.purchased,
                    border: COLORS.purchasedBorder,
                    glow: COLORS.purchasedGlow
                };
            case NODE_STATES.MAXED:
                return {
                    fill: COLORS.maxed,
                    border: COLORS.maxedBorder,
                    glow: COLORS.maxedGlow
                };
            default:
                return {
                    fill: COLORS.locked,
                    border: COLORS.lockedBorder,
                    glow: null
                };
        }
    }

    /**
     * Get colors for pending refund state
     */
    getPendingColors() {
        return {
            fill: COLORS.pending,
            border: COLORS.pendingBorder,
            glow: COLORS.pendingGlow
        };
    }

    /**
     * Draw lock icon
     */
    drawLockIcon(x, y) {
        const ctx = this.ctx;
        ctx.fillStyle = COLORS.textSecondary;
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🔒', x, y);
    }

    /**
     * Draw rank indicator (e.g., "2/3" or "2→0/3" if pending)
     */
    drawRankIndicator(x, y, currentRank, maxRank, state, pendingRanks = 0) {
        const ctx = this.ctx;

        // Position at bottom-right of node to avoid overlap with nodes below
        const indicatorX = x + NODE_RADIUS + 2;
        const indicatorY = y + NODE_RADIUS - 8;

        // Determine display text and width
        const isPending = pendingRanks > 0;
        const newRank = currentRank - pendingRanks;
        const displayText = isPending ? `${currentRank}→${newRank}/${maxRank}` : `${currentRank}/${maxRank}`;
        const pillWidth = isPending ? 52 : 36;

        // Background pill
        ctx.beginPath();
        ctx.roundRect(indicatorX - pillWidth / 2, indicatorY - 8, pillWidth, 16, 4);
        ctx.fillStyle = isPending ? 'rgba(239, 68, 68, 0.6)' : 'rgba(0, 0, 0, 0.6)';
        ctx.fill();

        // Text
        ctx.fillStyle = isPending ? '#ffffff' : (state === NODE_STATES.MAXED ? COLORS.maxedBorder : COLORS.text);
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(displayText, indicatorX, indicatorY);
    }

    /**
     * Get node at canvas coordinates
     */
    getNodeAtPosition(canvasX, canvasY) {
        // Transform canvas coords to world coords
        const worldX = (canvasX - this.offset.x) / this.scale;
        const worldY = (canvasY - this.offset.y) / this.scale;

        for (const [nodeId, node] of Object.entries(this.tree.nodes)) {
            const dx = worldX - node.position.x;
            const dy = worldY - node.position.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= NODE_RADIUS + 5) {
                return nodeId;
            }
        }

        return null;
    }

    /**
     * Handle mouse down for panning
     */
    onMouseDown(e) {
        this.isDragging = true;
        this.dragDistance = 0; // Reset drag distance
        this.lastMouse = { x: e.clientX, y: e.clientY };
    }

    /**
     * Handle mouse move for panning
     */
    onMouseMove(e) {
        if (!this.isDragging) return;

        const dx = e.clientX - this.lastMouse.x;
        const dy = e.clientY - this.lastMouse.y;

        // Track total drag distance
        this.dragDistance += Math.abs(dx) + Math.abs(dy);

        this.offset.x += dx;
        this.offset.y += dy;

        // Apply soft pan limits to keep tree partially visible
        this.clampOffset();

        this.lastMouse = { x: e.clientX, y: e.clientY };
        this.render();
    }

    /**
     * Clamp offset to keep tree at least partially visible
     */
    clampOffset() {
        if (!this.tree) return;

        const treeWidth = this.tree.canvasSize.width * this.scale;
        const treeHeight = this.tree.canvasSize.height * this.scale;
        const margin = 100; // Keep at least 100px of tree visible

        // Min offset: tree can't go too far right/down (left/top edge of tree stays in view)
        const minX = margin - treeWidth;
        const minY = margin - treeHeight;

        // Max offset: tree can't go too far left/up (right/bottom edge stays in view)
        const maxX = this.canvas.width - margin;
        const maxY = this.canvas.height - margin;

        this.offset.x = Math.max(minX, Math.min(maxX, this.offset.x));
        this.offset.y = Math.max(minY, Math.min(maxY, this.offset.y));
    }

    /**
     * Handle mouse up
     */
    onMouseUp() {
        this.isDragging = false;
    }

    /**
     * Check if the last interaction was a drag (moved more than threshold)
     * Used to prevent accidental clicks when dragging
     */
    wasDragging() {
        return this.dragDistance > 5; // 5px threshold
    }

    /**
     * Handle wheel for zooming
     */
    onWheel(e) {
        e.preventDefault();

        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.5, Math.min(2, this.scale * zoomFactor));

        // Zoom towards mouse position
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Adjust offset to zoom towards mouse
        this.offset.x = mouseX - (mouseX - this.offset.x) * (newScale / this.scale);
        this.offset.y = mouseY - (mouseY - this.offset.y) * (newScale / this.scale);

        this.scale = newScale;

        // Apply pan limits after zoom
        this.clampOffset();

        this.render();
    }

    /**
     * Reset view to default (centered)
     */
    resetView() {
        this.scale = 1;
        // Re-center the tree
        if (this.tree) {
            const treeWidth = this.tree.canvasSize.width;
            const treeHeight = this.tree.canvasSize.height;
            this.offset.x = (this.canvas.width - treeWidth) / 2;
            this.offset.y = (this.canvas.height - treeHeight) / 2;
        } else {
            this.offset = { x: 0, y: 0 };
        }
        this.render();
    }

    /**
     * Start animation loop
     */
    startAnimation() {
        const animate = () => {
            this.render();
            this.animationFrame = requestAnimationFrame(animate);
        };
        animate();
    }

    /**
     * Stop animation loop
     */
    stopAnimation() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        this.stopAnimation();
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
    }
}

/**
 * Create a renderer for a skill's talent tree
 */
export function createTalentTreeRenderer(canvas, skillId) {
    return new TalentTreeRenderer(canvas, skillId);
}
